// dependency-graph-ignore-file

import type { FireParams, MetricConfig, MetricMetadataBase } from './define-metric';
import { runStabilizedScheduler, type MetricRef } from './stabilized-scheduler';

export {
  createMetricRef,
  cancelMetric,
  STABILIZED_METRIC_SUPERSEDED_REASON,
  type MetricRef,
} from './stabilized-scheduler';

/**
 * Fires a metric using the stabilized probe model.
 *
 * Merges config-level metadata with ad-hoc metadata, builds the probe from config,
 * and starts the rAF-based stabilization loop. Use `useMetric` in React components.
 *
 * Pass `metadata.contentId` when you want database size (`entriesCount`, `fieldsCount`)
 * attached to the published payload.
 *
 * **Same `MetricRef`:** Starting a new fire while another run is in flight **silently** cancels
 * the prior rAF loop (no Reliability row for supersede); only the new interaction’s outcome
 * is published.
 *
 * **Stable ref:** `ref` must be the same object for every fire for that slot (typically
 * `createMetricRef()` held in module or instance scope, or the slot held for the hook
 * lifetime inside `useMetric` via `useMemo(() => createMetricRef(), [])`). Passing a fresh
 * `{ current: null }`
 * each call would break supersede/cancel bookkeeping.
 */
export function fireMetric<
  TData,
  TMetadata extends MetricMetadataBase = MetricMetadataBase
>(
  config: MetricConfig<TData, TMetadata>,
  params: FireParams<TData, TMetadata>,
  ref: MetricRef
): void {
  const { startTime, probeData, metadata, overrides, failImmediately } = params;
  const configMeta =
    metadata !== undefined ? config.buildMetadata?.(metadata) : undefined;
  const mergedMeta = { ...configMeta, ...metadata };

  runStabilizedScheduler(
    {
      startTime,
      metricName: config.metricName,
      getFrameState: config.buildProbe(probeData),
      metadata: mergedMeta,
      maxWaitMs: overrides?.maxWaitMs ?? config.maxWaitMs,
      stableFramesRequired:
        overrides?.stableFramesRequired ?? config.stableFramesRequired,
      failImmediately,
    },
    ref
  );
}
